import { useEffect, useRef, useState } from 'react';
import { SystemIcon } from '@monorepo/nova-icons';
import {
  DropdownContainer,
  DropdownOption,
  DropDownStyled,
} from './dashboard-dropdown.styles';

interface DashboardDropdownProps<T> {
  options: { label: string; value: T }[];
  onSelect: (value: T) => void;
  defaultValue?: T;
}

export const DashboardDropdown = <T,>({
  options,
  onSelect,
  defaultValue,
}: DashboardDropdownProps<T>) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedLabel, setSelectedLabel] = useState('');
  const [width, setWidth] = useState<number | undefined>();
  const hiddenRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const initialLabel =
      options.find((option) => option.value === defaultValue)?.label ||
      options[0]?.label;
    setSelectedLabel(initialLabel);
  }, [defaultValue, options]);

  useEffect(() => {
    if (hiddenRef.current) {
      setWidth(hiddenRef.current.offsetWidth);
    }
  }, [options]);

  const handleSelect = (option: { label: string; value: T }) => {
    onSelect(option.value);
    setSelectedLabel(option.label);
    setIsOpen(false);
  };

  return (
    <div style={{ position: 'relative', width: width ? `${width}px` : 'auto' }}>
      <div
        ref={hiddenRef}
        style={{
          position: 'absolute',
          visibility: 'hidden',
          whiteSpace: 'nowrap',
          padding: '0 24px',
          gap: '8px',
        }}
      >
        {
          options.reduce((prev, curr) =>
            prev.label.length > curr.label.length ? prev : curr,
          ).label
        }
        <SystemIcon iconName="CaretDown" />
      </div>

      <DropDownStyled
        direction="row"
        alignItems="center"
        onClick={() => setIsOpen(!isOpen)}
        style={{ width: '100%', justifyContent: 'space-between' }}
      >
        <span>{selectedLabel}</span>
        <SystemIcon
          iconName="CaretDown"
          sx={{
            fontSize: (theme) => theme.spacing(3),
            transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.3s ease-in-out',
          }}
        />
        {isOpen && (
          <DropdownContainer>
            {options.map((option) => (
              <DropdownOption
                key={String(option.value)}
                onClick={() => handleSelect(option)}
              >
                {option.label}
              </DropdownOption>
            ))}
          </DropdownContainer>
        )}
      </DropDownStyled>
    </div>
  );
};
